<template>
    <div class="tw-px-4 tw-pb-10">

        <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <div class="tw-flex tw-items-center tw-gap-2">
                <FormField v-model="search" type="text" :hide-label="true" :hide-errors="true" :placeholder="$t('common.categories-search-placeholder')"
                    left-icon="solar:magnifer-linear" @input="onSearch" class="tw-bg-white tw-rounded-md tw-min-w-[300px]" />

                <!-- Type Filter -->
                <FormSelect v-model="filters.type" :options="typeOptions"
                    :placeholder="$t('common.filter-by-type', 'Filter by type')"
                    left-icon="solar:filter-bold-duotone" class="tw-bg-white tw-rounded-md tw-min-w-[150px]"  />

            </div>

            <div>
                <a-button @click="showCreateModal = true" intent="flat" left-icon="solar:add-circle-bold-duotone"
                    class="tw-min-w-[160px]">
                    {{ $t('common.create-new-certificate') }}
                </a-button>
            </div>
        </div>

        <!-- Search and Filters Section -->
        <div v-if="false" class="tw-p-5 tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200 tw-mb-6">
            <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                <!-- Search Input -->


                <!-- Status Filter -->
                <div>
                    <FormSelect v-model="filters.status" :options="statusOptions"
                        :placeholder="$t('common.filter-by-status', 'Filter by status')"
                        left-icon="solar:filter-bold-duotone" @change="onFilterChange" />
                </div>
            </div>
        </div>

        <!-- Table Section -->
        
        <CertificateTable :loading="loading" :items="certificates" :options="paginationOptions" @next="onNext"
            @previous="onPrevious" @refresh="fetchCertificates" @edit="onEdit" @delete="onDelete" />

        <!-- Create Certificate Modal -->
        <CertificateCreateModal v-if="showCreateModal" :visible="showCreateModal" @close="showCreateModal = false"
            @created="onCertificateCreated" />

        <!-- Edit Certificate Modal -->
        <CertificateEditModal v-if="showEditModal" :visible="showEditModal" :certificate="selectedCertificate"
            @close="showEditModal = false" @updated="onCertificateUpdated" />
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import CertificateTable from '../certificates/table/CertificateTable.vue';
import CertificateCreateModal from '../certificates/modals/CertificateCreateModal.vue';
import CertificateEditModal from '../certificates/modals/CertificateEditModal.vue';
import certificatesApi from '@/api/certificates';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

// Reactive data
const loading = ref(false);
const certificates = ref([]);
const search = ref('');
const showCreateModal = ref(false);
const showEditModal = ref(false);
const selectedCertificate = ref(null);

// Pagination options
const paginationOptions = ref({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null
});

// Filters
const filters = reactive({
    status: '',
    type: ''
});

// Type options for filter
const typeOptions = ref([
    { id: '', name: t('common.all') },
    { id: 'product', name: t('common.product') },
    { id: 'service', name: t('common.service') },
    { id: 'company', name: t('common.company') },
    { id: 'general', name: t('common.general') }
]);


// Search debounce
let searchTimeout = null;
let filterTimeout = null;

// Watch for filter changes
watch(filters, () => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => {
        fetchCertificates(1);
    }, 300);
}, { deep: true });

// Methods
const fetchCertificates = async (page = 1) => {
    loading.value = true;

    try {
        const params = {
            page,
            per_page: paginationOptions.value.per_page,
            search: search.value,
            ...filters
        };

        const response = await certificatesApi.admin.paginate(params);

        if (response.data && response.data.data) {
            certificates.value = response.data.data;
            paginationOptions.value = {
                current_page: response.data.current_page,
                per_page: response.data.per_page,
                total: response.data.total,
                last_page: response.data.last_page,
                next_page_url: response.data.next_page_url,
                prev_page_url: response.data.prev_page_url
            };
        }
    } catch (error) {
        console.error('Error fetching certificates:', error);
        useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
    } finally {
        loading.value = false;
    }
};

const onSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        fetchCertificates(1);
    }, 500);
};

const onNext = () => {
    if (paginationOptions.value.current_page < paginationOptions.value.last_page) {
        fetchCertificates(paginationOptions.value.current_page + 1);
    }
};

const onPrevious = () => {
    if (paginationOptions.value.current_page > 1) {
        fetchCertificates(paginationOptions.value.current_page - 1);
    }
};

const onCertificateCreated = () => {
    showCreateModal.value = false;
    useAlert(t('messages.certificate-created'), 'success');
    fetchCertificates(paginationOptions.value.current_page);
};

const onCertificateUpdated = () => {
    showEditModal.value = false;
    selectedCertificate.value = null;
    useAlert(t('messages.certificate-updated'), 'success');
    fetchCertificates(paginationOptions.value.current_page);
};

const onEdit = (certificate) => {
    selectedCertificate.value = certificate;
    showEditModal.value = true;
};

const onDelete = () => {
    // The delete functionality is handled in CertificateTableActions component
    // This is just for any additional logic if needed
    fetchCertificates(paginationOptions.value.current_page);
};

// Initialize
onMounted(() => {
    fetchCertificates();
});
</script>

<style scoped>
/* Add any custom styles here */
</style>